import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Share2, Users } from "lucide-react";
import { toast } from "sonner";

export const inviteUrl = (code) => `${window.location.origin}/?ref=${code}`;

export const InviteCard = ({ code, referrals = 0, pending = 0, spots = 5, prefix, dark = false }) => {
  const [copied, setCopied] = useState(false);
  const url = inviteUrl(code);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — press and hold the link to copy it");
    }
  };

  const share = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title: "Lunelle", text: "I just joined the Lunelle waitlist — a cycle companion for body, soul and sky. Join me:", url });
    } catch {
      // user closed the share sheet
    }
    return undefined;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}
      className={`mt-3 rounded-2xl border p-4 ${dark ? "border-white/15 bg-white/5" : "border-[#C86D51]/20 bg-white/70"}`} data-testid={`${prefix}-invite-card`}>
      <p className={`text-sm leading-relaxed ${dark ? "text-[#F4DED3]" : "text-[#3D302B]"}`}>
        <span className="font-semibold text-[#9E4429]">Move up {spots} spots</span> for every friend who joins with your link and confirms their email.
      </p>
      <div className={`mt-3 flex items-center gap-2 rounded-full border py-1 pl-4 pr-1 ${dark ? "border-white/15" : "border-[#C86D51]/20 bg-[#FDFBF7]"}`}>
        <span className={`min-w-0 flex-1 truncate text-xs ${dark ? "text-[#d9c7bd]" : "text-[#5C4A42]"}`} data-testid={`${prefix}-invite-link`}>{url}</span>
        <button type="button" onClick={copy} data-testid={`${prefix}-invite-copy-button`} aria-label="Copy invite link"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2C221E] text-[#FBF5EE] transition-colors hover:bg-[#9E4429]">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={share} data-testid={`${prefix}-invite-share-button`}
          className="flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-[#9E4429] px-3 text-xs font-semibold text-[#FBF5EE] transition-colors hover:bg-[#843722]">
          <Share2 className="h-3.5 w-3.5" /> Share
        </button>
      </div>
      <p className={`mt-3 flex items-center gap-2 text-xs ${dark ? "text-[#d9c7bd]" : "text-[#6B5A52]"}`} data-testid={`${prefix}-invite-referrals`}>
        <Users className="h-3.5 w-3.5" /> {referrals} {referrals === 1 ? "friend has" : "friends have"} joined with your link{pending > 0 ? ` · ${pending} waiting to confirm` : ""}
      </p>
    </motion.div>
  );
};
